import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { I18nProvider } from "@/context/I18nContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute, { AdminRoute, UserAdminRoute, SystemAdminRoute, OpsDashboardRoute, CctvHubRoute, KpiRoute, TabRoute } from "@/components/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import SalesHub from "@/pages/SalesHub";
import FinancialsHub from "@/pages/FinancialsHub";
import QualityHub from "@/pages/QualityHub";
import CctvHub from "@/pages/CctvHub";
import OpsDashboardPage from "@/pages/OpsDashboardPage";
import BranchKpiPage from "@/pages/BranchKpiPage";
import UsersPage from "@/pages/admin/UsersPage";
import BranchesPage from "@/pages/admin/BranchesPage";
import SalesSyncPage from "@/pages/admin/SalesSyncPage";
import BackupsPage from "@/pages/admin/BackupsPage";
import IngestStatusPage from "@/pages/admin/IngestStatusPage";
import DataAuditPage from "@/pages/admin/DataAuditPage";
import WasteMealsHub from "@/pages/WasteMealsHub";
import AiAnalysisPage from "@/pages/AiAnalysisPage";
import MaintenancePage from "@/pages/MaintenancePage";
import CashierCheckPage from "@/pages/CashierCheckPage";
import HrIntegrationPage from "@/pages/admin/HrIntegrationPage";
import NoAccessPage from "@/pages/NoAccessPage";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";

function Landing() {
    const { me, canOpsDashboard, isSystemAdmin, canBranchCheckin, canCctvManage, canQcViolationManage } = useAuth();
    // Anyone who can open the Operations Dashboard lands there.
    if (canOpsDashboard) return <Navigate to="/dashboard" replace />;
    // Otherwise: first permitted, routable module (registry-driven).
    const nav = me?.nav || [];
    const first = nav.find((t) => t.permitted && t.route);
    if (first) return <Navigate to={first.route} replace />;
    // Capability-only users (no routable module tab) land on their screen.
    if (canCctvManage) return <Navigate to="/cctv" replace />;
    if (canBranchCheckin) return <Navigate to="/cctv?tab=checkin" replace />;
    if (canQcViolationManage) return <Navigate to="/quality?tab=violations" replace />;
    if (isSystemAdmin) return <Navigate to="/admin/ingest-status" replace />;
    return <Navigate to="/no-access" replace />;
}

/**
 * Legacy route redirect. Older nav paths (`/qc-inspection`, `/qc-waste`) and
 * every email deep-link that was previously sent to those paths need to keep
 * working after the QC nav merge. This helper redirects to `/quality`,
 * preserving the query string so `?round=<id>` still opens the correct
 * inspection round via QualityHub's deep-link handler.
 */
function LegacyRedirect({ tab }) {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    if (tab) params.set("tab", tab);
    const search = params.toString();
    return <Navigate to={`/quality${search ? `?${search}` : ""}`} replace />;
}

/**
 * Route gate that permits access when EITHER of the given tab keys is
 * granted. Used by the merged Quality Hub which accepts users with
 * `qc_inspection` OR `qc_waste`.
 */
function QualityTabRoute({ children }) {
    const { canQcViolationManage } = useAuth();
    return (
        <TabRoute tabKey="qc_inspection" fallbackTabKey="qc_waste" allowExtra={canQcViolationManage}>
            {children}
        </TabRoute>
    );
}

/**
 * <Routes> inside a render-error boundary that clears itself on navigation:
 * a page that throws shows the fallback (reload / home) instead of blanking
 * the app, and going back or opening another page recovers without a reload.
 * The Toaster stays outside, so toasts keep working.
 */
function SafeRoutes({ children }) {
    const { pathname } = useLocation();
    return (
        <ErrorBoundary resetKey={pathname}>
            <Routes>{children}</Routes>
        </ErrorBoundary>
    );
}

export default function App() {
    return (
        <I18nProvider>
            <AuthProvider>
                <BrowserRouter>
                    <SafeRoutes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route element={<ProtectedRoute />}>
                            <Route element={<AppShell />}>
                                <Route path="/" element={<Landing />} />
                                <Route
                                    path="/dashboard"
                                    element={
                                        <OpsDashboardRoute>
                                            <OpsDashboardPage />
                                        </OpsDashboardRoute>
                                    }
                                />
                                <Route
                                    path="/branch-checkin"
                                    element={<Navigate to="/cctv?tab=checkin" replace />}
                                />
                                <Route
                                    path="/branch-kpi"
                                    element={
                                        <KpiRoute>
                                            <BranchKpiPage />
                                        </KpiRoute>
                                    }
                                />
                                <Route
                                    path="/sales"
                                    element={
                                        <TabRoute tabKey="sales">
                                            <SalesHub />
                                        </TabRoute>
                                    }
                                />
                                <Route
                                    path="/financials"
                                    element={
                                        <TabRoute tabKey="financials">
                                            <FinancialsHub />
                                        </TabRoute>
                                    }
                                />
                                <Route
                                    path="/quality"
                                    element={
                                        <QualityTabRoute>
                                            <QualityHub />
                                        </QualityTabRoute>
                                    }
                                />
                                <Route
                                    path="/cctv"
                                    element={
                                        <CctvHubRoute>
                                            <CctvHub />
                                        </CctvHubRoute>
                                    }
                                />
                                <Route
                                    path="/waste-meals"
                                    element={
                                        <TabRoute tabKey="waste_meals">
                                            <WasteMealsHub />
                                        </TabRoute>
                                    }
                                />
                                {/* «التحليل الذكي» — opens only through `ai_analysis_view` (cap-only tab). */}
                                <Route
                                    path="/ai-analysis"
                                    element={
                                        <TabRoute tabKey="ai_analysis">
                                            <AiAnalysisPage />
                                        </TabRoute>
                                    }
                                />
                                {/* «الصيانة» — opens only through `maintenance_view` (cap-only tab). */}
                                <Route
                                    path="/maintenance"
                                    element={
                                        <TabRoute tabKey="maintenance">
                                            <MaintenancePage />
                                        </TabRoute>
                                    }
                                />
                                {/* «متوسط فاتورة الكاشير» — opens only through `cashier_check_view` (cap-only tab). */}
                                <Route
                                    path="/cashier-check"
                                    element={
                                        <TabRoute tabKey="cashier_check">
                                            <CashierCheckPage />
                                        </TabRoute>
                                    }
                                />
                                {/* Legacy paths — keep working forever for old email deep-links */}                                <Route path="/qc-inspection" element={<LegacyRedirect />} />
                                <Route path="/qc-waste" element={<LegacyRedirect tab="waste" />} />
                                <Route
                                    path="/admin/users"
                                    element={
                                        <UserAdminRoute>
                                            <UsersPage />
                                        </UserAdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/branches"
                                    element={
                                        <SystemAdminRoute>
                                            <BranchesPage />
                                        </SystemAdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/sales-sync"
                                    element={
                                        <SystemAdminRoute>
                                            <SalesSyncPage />
                                        </SystemAdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/backups"
                                    element={
                                        <SystemAdminRoute>
                                            <BackupsPage />
                                        </SystemAdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/ingest-status"
                                    element={
                                        <SystemAdminRoute>
                                            <IngestStatusPage />
                                        </SystemAdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/hr-integration"
                                    element={
                                        <AdminRoute>
                                            <HrIntegrationPage />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/data-audit"
                                    element={
                                        <AdminRoute>
                                            <DataAuditPage />
                                        </AdminRoute>
                                    }
                                />
                                <Route path="/no-access" element={<NoAccessPage />} />
                            </Route>
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </SafeRoutes>
                    <Toaster position="top-center" richColors closeButton />
                </BrowserRouter>
            </AuthProvider>
        </I18nProvider>
    );
}
